import { Activity, Building2, LayoutDashboard, Package, Users } from 'lucide-react'
import { NavLink } from 'react-router-dom'

import type { LucideIcon } from 'lucide-react'
import type { ReactElement } from 'react'

import enjoyLogoWhite from '@/assets/enjoy-logo-white.webp'
import { cn } from '@/lib/cn'
import { hasAnyPermission } from '@/lib/permissions'
import { useAuth } from '@/providers/auth-context'

interface SidebarItem {
  to: string
  label: string
  icon: LucideIcon
  /** Permissões necessárias (any-of). Array vazio = público pros logados. */
  requiresAny: readonly string[]
}

const ITEMS: readonly SidebarItem[] = [
  { to: '/app', label: 'Dashboard', icon: LayoutDashboard, requiresAny: ['admin:ecosystem'] },
  { to: '/app/admin/users', label: 'Usuarios', icon: Users, requiresAny: ['admin:users'] },
  { to: '/app/admin/ecosystem/units', label: 'Unidades', icon: Building2, requiresAny: ['admin:ecosystem'] },
  { to: '/app/admin/ecosystem/products', label: 'Produtos', icon: Package, requiresAny: ['admin:ecosystem'] },
  { to: '/app/admin/ecosystem/tracking', label: 'Rastreamento', icon: Activity, requiresAny: ['admin:ecosystem'] },
]

export function Sidebar(): ReactElement {
  const { user } = useAuth()
  const userPerms = user?.permissions ?? []

  return (
    <aside className="sticky top-0 flex h-screen w-60 shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground">
      <div className="flex items-center gap-3 px-6 py-5">
        <img
          src={enjoyLogoWhite}
          alt="Enjoy"
          className="h-7 w-auto max-w-full"
          width={105}
          height={28}
        />
        <span className="border-l border-sidebar-border pl-3 text-[0.62rem] font-medium uppercase tracking-[0.22em] text-muted-foreground">
          Painel
        </span>
      </div>

      <nav className="flex-1 space-y-1 px-3 pt-2">
        {ITEMS.filter((item) => hasAnyPermission(userPerms, item.requiresAny)).map((item) => {
          const Icon = item.icon
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md border-l-2 px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'border-gold bg-sidebar-accent font-medium text-sidebar-accent-foreground'
                    : 'border-transparent text-sidebar-foreground/75 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{item.label}</span>
            </NavLink>
          )
        })}
      </nav>

      <div className="border-t border-sidebar-border px-6 py-4">
        <p className="font-display text-[0.72rem] italic text-eco-muted">Ecossistema Enjoy</p>
      </div>
    </aside>
  )
}
